import { USER_TYPE, useAuthStoreShallow } from "@features/authentication";
import type { IOnboardingStep } from "@features/onboarding";
import { useAppSelector } from "@store";
import { getAccount, getSubscriptionPlan } from "@store/account/selectors";
import { useMemo } from "react";
import { PLATFORM_CHECKLIST_TASKS, type IPlatformTask } from "./checklist";
import { ONBOARDING_DECKS_ENABLED } from "./constants";
import { PLATFORM_STEPS } from "./steps";
import {
  buildTasks,
  resolveTaskSet,
  type TOnboardingPlan,
  type TOnboardingRole,
} from "./task-sets";
import { ONBOARDING_VARIANTS, type TOnboardingVariant } from "./variants";

type TVariantConfig = (typeof ONBOARDING_VARIANTS)[TOnboardingVariant];

interface IVariantOnboarding {
  /** False until the auth claims land. Surfaces must render nothing while it is false. */
  isReady: boolean;
  variant: TOnboardingVariant | null;
  tasks: IPlatformTask[];
  steps: IOnboardingStep[];
  total: number;
  config: TVariantConfig | null;
  /** "plan/role" the task list was resolved from, e.g. "trial/admin"; analytics only. */
  taskSet: string | null;
}

const EMPTY_TASKS: IPlatformTask[] = [];
const EMPTY_STEPS: IOnboardingStep[] = [];

const NOT_READY: IVariantOnboarding = {
  isReady: false,
  variant: null,
  tasks: EMPTY_TASKS,
  steps: EMPTY_STEPS,
  total: 0,
  config: null,
  taskSet: null,
};

export const useOnboardingVariant = (): TOnboardingVariant | null => {
  const { claims, userType, isAdminCore } = useAuthStoreShallow((s) => ({
    claims: s.claims,
    userType: s.userType,
    isAdminCore: s.isAdminCore,
  }));
  if (!claims) return null;
  if (isAdminCore) return "admin";
  return userType === USER_TYPE.ADMINISTRATOR ||
    userType === USER_TYPE.ORG_ADMIN
    ? "admin"
    : "invited";
};

/** Plan as the feature locks resolve it (store/subscription getPresetFeature). */
const useOnboardingPlan = (): TOnboardingPlan | null => {
  const account = useAppSelector(getAccount);
  const subscriptionPlan = useAppSelector(getSubscriptionPlan);
  if (!account) return null;
  if (account.plan !== "paid" && account.plan !== undefined) return "trial";
  if (!account.isSelfServ) return "team";
  const title = subscriptionPlan?.title?.toLowerCase();
  return title === "starter" ? "starter" : "pro";
};

const useOnboardingRole = (): TOnboardingRole | null => {
  const { claims, userType } = useAuthStoreShallow((s) => ({
    claims: s.claims,
    userType: s.userType,
  }));
  if (!claims) return null;
  switch (userType) {
    case USER_TYPE.USER:
      return "user";
    case USER_TYPE.CREATOR_LITE:
    case USER_TYPE.CREATOR_LITE_OLD:
      return "creator-lite";
    case USER_TYPE.CREATOR:
      return "creator";
    default:
      return "admin";
  }
};

/** The role flag the onboarding surfaces gate on. */
export const useOnboardingIsRestrictedRole = (): boolean =>
  useAuthStoreShallow((s) => s.isUserOrCreatorLite);

export const useVariantOnboarding = (): IVariantOnboarding => {
  const variant = useOnboardingVariant();
  const plan = useOnboardingPlan();
  const role = useOnboardingRole();
  return useMemo(() => {
    if (!variant || !plan || !role) return NOT_READY;
    const config = ONBOARDING_VARIANTS[variant];
    const tasks: IPlatformTask[] = buildTasks(
      resolveTaskSet(plan, role),
      PLATFORM_CHECKLIST_TASKS,
    );
    const steps = PLATFORM_STEPS.filter(
      (step) =>
        !config.excludedStepKeys.includes(step.key) &&
        (ONBOARDING_DECKS_ENABLED || step.key !== "starter-decks"),
    );
    return {
      isReady: true,
      variant,
      tasks,
      steps,
      total: tasks.length,
      config,
      taskSet: `${plan}/${role}`,
    };
  }, [variant, plan, role]);
};
